import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogIn, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Default landing path per role
  const roleHome = (role) => {
    if (role === 'chef') return '/chef';
    if (role === 'employe' || role === 'service') return '/employe';
    if (role === 'responsable') return '/responsable';
    // patron, admin, manager -> Hub by default
    return '/hub';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      const data = res.data;
      const token = data.token;
      const u = data.user || data;
      const user = { id: u.id, email: u.email, nom: u.nom, role: u.role, name: u.nom };
      localStorage.setItem('lemas_token', token);
      localStorage.setItem('lemas_user', JSON.stringify(user));
      toast.success(`Bienvenue, ${user.nom}`);
      // Redirect: explicit ?redirect= param wins, else land on role-specific page
      navigate(redirect || roleHome(user.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || '').join(' ')
        : 'Erreur de connexion';
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] mb-6">
            <ArrowLeft size={12} /> Retour
          </Link>
          <h1 className="font-title text-xl tracking-[0.2em] text-[rgb(var(--lm-gold))]">LE MAS</h1>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Connexion a votre espace</p>
        </div>

        <form onSubmit={handleLogin} className="lm-card p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-sm bg-[rgb(var(--lm-red)/0.08)] text-xs text-[rgb(var(--lm-red))]" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
              placeholder="votre@email.com" autoFocus data-testid="login-email" />
          </div>

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Mot de passe</label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] pr-9"
                placeholder="••••••" data-testid="login-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading || !email || !password}
            className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 py-5 gap-2"
            data-testid="login-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
            Se connecter
          </Button>

          <p className="text-center text-xs text-[rgb(var(--lm-text-muted))]">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-[rgb(var(--lm-gold))] hover:underline" data-testid="goto-register">
              S'inscrire
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
